export interface Streamer {
    readonly streamerId: string;
    readonly handle: string;
    readonly displayName: string;
    readonly admin: boolean;
    readonly status: string;
    readonly moderationStatus: string;
    readonly moderationStrikes: number;
    readonly requiresReprovision: boolean;
    readonly moderationReason: string | null;
    readonly streamLimitBlockedUntil: string | null;
    readonly streamRestartDelaySeconds: number | null;
    readonly maxSessionDurationSeconds: number;
    readonly nextBroadcastAllowedAt: string | null;
    readonly broadcastBlockReason: BroadcastBlockReason;
    readonly membershipAccess?: MembershipAccess;
    readonly broadcastSetupReady: boolean;
    readonly canBroadcast: boolean;
    readonly createdAt: string;
    readonly updatedAt: string;
    readonly playbackUrl: string | null;
    readonly ingestEndpoint: string | null;
}

export type BroadcastBlockReason = "setup_not_ready" | "active_session" | "restart_wait" | "duration_limit_cooldown" | "membership_required" | "membership_unavailable" | null;

export interface MembershipAccess {
    readonly enforcementEnabled: boolean;
    readonly requiredFeatureId: string | null;
    readonly status: "available" | "missing_required_feature" | "verification_unavailable";
}

export interface BroadcastCredentials {
    readonly streamerId: string;
    readonly ingestEndpoint: string;
    readonly playbackUrl: string | null;
    readonly streamKeyValue: string;
}

export interface Session {
    readonly sessionId: string;
    readonly streamerId: string;
    readonly playbackUrl: string | null;
    readonly recordingPlaybackUrl: string | null;
    readonly recordingThumbnailUrl: string | null;
    readonly recordingRemoved: boolean;
    readonly status: string;
    readonly recordingStatus: string;
    readonly moderationStatus: string;
    readonly moderationReason: string | null;
    readonly reviewOffsetSeconds: number | null;
    readonly isLive: boolean;
    readonly canPlay: boolean;
    readonly canDelete: boolean;
    readonly playbackKind: "live" | "recording" | null;
    readonly recordingDurationMs: number | null;
    readonly recordingRetentionDays: number | null;
    readonly recordingExpiresAt: string | null;
    readonly recordingExpired: boolean;
    readonly startedAt: string;
    readonly endedAt: string | null;
    readonly durationSeconds: number | null;
    readonly endReason: string | null;
    readonly createdAt: string;
    readonly updatedAt: string;
}

export interface AdminReviewData {
    readonly streamers: Streamer[];
    readonly sessions: Session[];
}

export interface EnsureMyStreamerInput {
    readonly handle: string;
    readonly displayName: string;
}

interface CollectionResponse<TItem> {
    readonly items: TItem[];
}

interface ErrorResponse {
    readonly message?: string;
}

interface AuthTokenSource {
    getAccessToken(): string | null;
    clear(): void;
}

const apiBasePath = "/api/v2.0/livestreaming";

export class LivestreamingApiError extends Error {
    public readonly statusCode: number;

    constructor(message: string, statusCode: number) {
        super(message);
        this.name = "LivestreamingApiError";
        this.statusCode = statusCode;
    }
}

export class LivestreamingApiClient {
    private readonly baseUrl: string;
    private readonly authTokenSource: AuthTokenSource;

    constructor(baseUrl: string, authTokenSource: AuthTokenSource) {
        this.baseUrl = baseUrl.replace(/\/$/, "");
        this.authTokenSource = authTokenSource;
    }

    public async getHealth(): Promise<{ status: string; service: string; }> {
        return this.request(`${apiBasePath}/health`, {
            requiresAuth: false,
        });
    }

    public async listStreamers(): Promise<Streamer[]> {
        const response = await this.request<CollectionResponse<Streamer>>(`${apiBasePath}/streamers`);
        return response.items;
    }

    public async getMyStreamer(): Promise<Streamer> {
        return this.request(`${apiBasePath}/me/streamer`);
    }

    public async ensureMyStreamer(input: EnsureMyStreamerInput): Promise<Streamer> {
        return this.request(`${apiBasePath}/me/streamer`, {
            method: "PUT",
            body: JSON.stringify(input),
        });
    }

    public async getMyStreamerSessions(): Promise<Session[]> {
        const response = await this.request<CollectionResponse<Session>>(`${apiBasePath}/me/sessions`);
        return response.items;
    }

    public async getAdminReview(): Promise<AdminReviewData> {
        return this.request(`${apiBasePath}/admin/review`);
    }

    public async getStreamerSessions(streamerId: string): Promise<Session[]> {
        const response = await this.request<CollectionResponse<Session>>(`${apiBasePath}/streamers/${encodeURIComponent(streamerId)}/sessions`);
        return response.items;
    }

    public async getMyBroadcastCredentials(): Promise<BroadcastCredentials> {
        return this.request(`${apiBasePath}/me/broadcast-credentials`, {
            method: "POST",
        });
    }

    public async listLiveSessions(): Promise<Session[]> {
        const response = await this.request<CollectionResponse<Session>>(`${apiBasePath}/sessions/live`);
        return response.items;
    }

    public async deleteSession(sessionId: string): Promise<void> {
        await this.request(`${apiBasePath}/sessions/${encodeURIComponent(sessionId)}`, {
            method: "DELETE",
        });
    }

    public async restoreSession(sessionId: string): Promise<Session> {
        return this.request(`${apiBasePath}/admin/sessions/${encodeURIComponent(sessionId)}/restore`, {
            method: "POST",
        });
    }

    public async removeRecordingForModeration(sessionId: string): Promise<Session> {
        return this.request(`${apiBasePath}/admin/sessions/${encodeURIComponent(sessionId)}/remove-recording`, {
            method: "POST",
        });
    }

    public async restoreStreamerAccess(streamerId: string): Promise<Streamer> {
        return this.request(`${apiBasePath}/admin/streamers/${encodeURIComponent(streamerId)}/restore-access`, {
            method: "POST",
        });
    }

    private async request<TResponse>(
        path: string,
        options: (RequestInit & { requiresAuth?: boolean; }) = {},
    ): Promise<TResponse> {
        const headers = new Headers(options.headers ?? undefined);
        const requiresAuth = options.requiresAuth ?? true;

        if (options.body && !headers.has("content-type")) {
            headers.set("content-type", "application/json");
        }

        if (requiresAuth) {
            const accessToken = this.authTokenSource.getAccessToken();
            if (!accessToken) {
                throw new LivestreamingApiError("You are not signed in", 401);
            }

            headers.set("authorization", `Bearer ${accessToken}`);
        }

        const response = await fetch(`${this.baseUrl}${path}`, {
            ...options,
            headers,
        });

        if (response.status === 204) {
            return undefined as TResponse;
        }

        const responseText = await response.text();
        const responseJson = responseText ? JSON.parse(responseText) as TResponse | ErrorResponse : undefined;

        if (!response.ok) {
            if (response.status === 401) {
                this.authTokenSource.clear();
            }

            const errorMessage = isErrorResponse(responseJson) ? responseJson.message ?? "Request failed" : "Request failed";
            throw new LivestreamingApiError(errorMessage, response.status);
        }

        return responseJson as TResponse;
    }
}

function isErrorResponse(value: unknown): value is ErrorResponse {
    return typeof value === "object" && value !== null && "message" in value;
}
